import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { MyParams, auditApi } from 'App';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  AnomalyAPI,
  convertTimeToSec,
  fullDateTime,
  Nullable,
  scrollToNewElement,
  timelineLabel,
  updateAnomalyPayload,
} from '@shared';

import { Breadcrumb, SelectAnomaly, SubHeading, Time } from '../../shared';
import { ConfirmedAnomaliesPropsType } from '../../../AnomaliesSidebar.types';
import { signatureProps } from './AnomalyDetails.styled';
import Timepicker from './Timepicker';
import { AnomalyUI } from '../ConfirmedAnomalies';

export function AnomalyDetails(
  props: Pick<ConfirmedAnomaliesPropsType, 'anomalyTypes'> & {
    currentAnomaly: AnomalyUI;
    handleClose: () => void;
    setCurrentAnomaly: Dispatch<SetStateAction<AnomalyUI>>;
  }
) {
  const { anomalyTypes, handleClose, currentAnomaly, setCurrentAnomaly } = props;

  const queryClient = useQueryClient();

  const { audit: auditUUID } = useParams<keyof MyParams>() as MyParams;

  const [commentValue, setCommentValue] = useState('');
  const { mutate: addComment } = useMutation({
    mutationFn: () => auditApi.addCommentToAnomaly(auditUUID, currentAnomaly.uuid, commentValue),
    onSuccess: (data) => {
      queryClient.setQueryData(['anomalies', auditUUID], (oldData: Nullable<AnomalyAPI[]>) => {
        return oldData?.map((anomaly) => {
          if (anomaly.uuid === currentAnomaly.uuid) {
            return { ...anomaly, comments: [...anomaly.comments, data] };
          } else return anomaly;
        });
      });
      setCurrentAnomaly((prevState) => ({
        ...prevState,
        comments: [...prevState.comments, data],
      }));
      setCommentValue('');
      scrollToNewElement('anomaly_comment');
    },
  });

  const [isShowEdit, setShowEdit] = useState(false);
  const [isShowRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  const { mutate: updateAnomaly } = useMutation({
    mutationFn: (payload: updateAnomalyPayload) =>
      auditApi.updateAnomaly(auditUUID, currentAnomaly.uuid, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['anomalies', auditUUID], (oldData: Nullable<AnomalyAPI[]>) =>
        oldData?.map((item) => (item.uuid === data.uuid ? data : item))
      );
      setShowEdit(false);
      setCurrentAnomaly((prevState) => ({
        ...prevState,
        start: data.startOffset,
        label: data.anomalyType.label,
        id: data.anomalyType.id,
      }));
    },
  });

  const { mutate: deleteAnomaly } = useMutation({
    mutationFn: () => auditApi.deleteAnomaly(auditUUID, currentAnomaly.uuid),
    onSuccess: (data) => {
      queryClient.setQueryData(['anomalies', auditUUID], data);
      setShowRemoveConfirmation(false);
      handleClose();
    },
  });
  const { values, handleChange, handleSubmit, resetForm } = useFormik({
    initialValues: {
      id: currentAnomaly.anomalyTypeId,
      startOffset: timelineLabel(currentAnomaly.start),
    },
    onSubmit: ({ id, startOffset }) => {
      updateAnomaly({ id, startOffset: convertTimeToSec(startOffset) });
    },
  });

  if (isShowRemoveConfirmation) {
    return (
      <Card variant='outlined'>
        <CardContent
          component='form'
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Breadcrumb
            backTo={'Anomaly Details'}
            handleClose={() => setShowRemoveConfirmation(false)}
          />
          <SubHeading title={'anomaly delete'} />
          <Typography sx={{ opacity: 0.8 }}>
            Are you sure you want to delete this anomaly? All data will be lost.
          </Typography>
          <Stack direction='row' justifyContent='space-between' gap={1.5}>
            <Button fullWidth onClick={() => setShowRemoveConfirmation(false)} variant='contained'>
              Cancel
            </Button>
            <Button fullWidth onClick={() => deleteAnomaly()} variant='contained' color='error'>
              Yes, Delete
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (isShowEdit) {
    return (
      <Card variant='outlined'>
        <CardContent
          component='form'
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <Breadcrumb
            backTo={'Anomaly Details'}
            handleClose={() => {
              setShowEdit(false);
              resetForm();
            }}
          />
          <SubHeading title={'anomaly edit'} />
          <SelectAnomaly anomalyTypes={anomalyTypes} id={values.id} handleChange={handleChange} />
          <Timepicker value={values.startOffset} handleChange={handleChange} />
          <Button type='submit' variant='contained'>
            Save
          </Button>
        </CardContent>
      </Card>
    );
  }

  const comments = currentAnomaly.comments;

  return (
    <Card variant='outlined'>
      <CardContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        component='form'
        onSubmit={handleSubmit}
        noValidate
      >
        <Breadcrumb backTo={'Confirmed Anomalies'} handleClose={handleClose} />
        <SubHeading title={currentAnomaly.label} />
        <Stack direction='row' justifyContent='space-between'>
          <Time fontSize={18} time={currentAnomaly.start} />
          <div>
            <IconButton onClick={() => setShowEdit(true)}>
              <EditIcon fontSize='small' color='primary' />
            </IconButton>
            <IconButton onClick={() => setShowRemoveConfirmation(true)}>
              <DeleteForeverIcon fontSize='small' color='error' />
            </IconButton>
          </div>
        </Stack>
        <List sx={{ height: 'calc(100vh - 440px)', overflow: 'auto' }}>
          {comments && comments.length < 1 && <Typography>No Comments</Typography>}
          {comments?.map((comment) => (
            <ListItem key={comment.uuid} disablePadding>
              <ListItemText
                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                id='anomaly_comment'
                primary={<Typography component='i'>{comment.comment}</Typography>}
                secondary={`- ${comment.firstName} ${comment.lastName} (${fullDateTime(
                  comment.timestamp
                )})`}
                secondaryTypographyProps={signatureProps}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
      {currentAnomaly.editableComment && (
        <CardActions sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label='Comments'
            value={commentValue}
            onChange={(e) => setCommentValue(e.target.value)}
          />
          <Button disabled={!commentValue} variant='contained' onClick={() => addComment()}>
            Comment
          </Button>
        </CardActions>
      )}
    </Card>
  );
}
